import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  School as SchoolIcon,
} from '@mui/icons-material';

interface ProfilePreviewModalProps {
  open: boolean;
  onClose: () => void;
  userData: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    location?: string;
    bio?: string;
    interests?: string[];
    workExperience?: string;
    education?: string;
    avatarUrl?: string;
  } | null;
}

const ProfilePreviewModal: React.FC<ProfilePreviewModalProps> = ({
  open,
  onClose,
  userData,
}) => {
  if (!userData) return null;

  const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Unknown User';
  const initials = `${userData.firstName?.[0] || ''}${userData.lastName?.[0] || ''}`.toUpperCase();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        }
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 1)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Profile Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 4,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
            }}
          />
          
          {/* Profile Picture */}
          <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
            <Avatar
              src={userData.avatarUrl}
              sx={{
                width: 120,
                height: 120,
                border: '4px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                background: userData.avatarUrl 
                  ? 'transparent'
                  : 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
              }}
            >
              {!userData.avatarUrl && (
                <PersonIcon sx={{ fontSize: 60, color: '#667eea' }} />
              )}
            </Avatar>
            
            {/* Online Status Indicator */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: '3px solid #ffffff',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)',
              }}
            />
          </Box>

          {/* Name and Title */}
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ color: 'white', mb: 1 }}
          >
            {fullName}
          </Typography>
          
          <Typography
            variant="body1"
            sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}
          >
            {userData.email}
          </Typography>

          {userData.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <LocationIcon sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {userData.location}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Profile Content */}
        <CardContent sx={{ p: 3 }}>
          {/* Bio Section */}
          {userData.bio && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#1e293b' }}>
                About
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                {userData.bio}
              </Typography>
            </Box>
          )}

          {/* Interests Section */}
          {userData.interests && userData.interests.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1e293b' }}>
                Interests
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {userData.interests.map((interest, index) => (
                  <Chip
                    key={index}
                    label={interest}
                    sx={{
                      background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                      color: '#3730a3',
                      fontWeight: 500,
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #c7d2fe 0%, #a5b4fc 100%)',
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Work Experience */}
          {userData.workExperience && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WorkIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#1e293b' }}>
                  Work Experience
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                {userData.workExperience}
              </Typography>
            </Box>
          )}

          {/* Education */}
          {userData.education && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SchoolIcon sx={{ color: '#10b981', fontSize: 20 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#1e293b' }}>
                  Education
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                {userData.education}
              </Typography>
            </Box>
          )}

          {/* Contact Information */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
              border: '1px solid rgba(148, 163, 184, 0.2)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center' }}>
              This is a preview of the user's profile. 
              {userData.interests && userData.interests.length > 0 && ' Click "View Profile" to see more details.'}
            </Typography>
          </Box>
        </CardContent>
      </DialogContent>
    </Dialog>
  );
};

export default ProfilePreviewModal;
